/**
 * Suchen und ausführen (S27). Sascha: „Ein Feld, in das man tippt, was man
 * will (‚trennen‘, ‚exportieren‘), statt es zu suchen.“
 *
 * Hier steht das Suchen -- und nur das. **Was** ein Befehl tut, weiss der
 * Schnittplatz; er gibt die Liste mit. Ohne Browser gerechnet, damit sich
 * prüfen lässt, was am Ende über die Nützlichkeit entscheidet: ob das
 * Richtige oben steht.
 *
 * Die Regeln, und jede hat einen Grund:
 *
 *  * **Alle getippten Wörter müssen vorkommen**, nicht eines davon. Wer „ton
 *    export“ tippt, meint beides; eine Oder-Suche wirft ihm die halbe Liste
 *    hin.
 *  * **Gleichgültig, ob gross oder klein, mit oder ohne Umlaut.** „uebergang“
 *    findet „Übergang“, „ss“ findet „ß“. Wer ein Feld zum Tippen aufmacht,
 *    tippt nicht sorgfältig.
 *  * **Ein Wortanfang zählt mehr als eine Stelle in der Mitte.** „ton“ soll
 *    „Ton-Werkzeuge“ finden und nicht „Automatisch speichern“ -- da steckt
 *    „ton“ auch drin.
 *  * **Der Name zählt mehr als ein Stichwort.** Stichwörter sind dafür da, dass
 *    „schneiden“ den Befehl „Trennen“ findet; sie sollen ihn aber nicht vor
 *    einen schieben, der wirklich so heisst.
 *  * **Was nicht geht, steht nicht da.** Ein Befehl, der gerade unmöglich ist
 *    (nichts gewählt, kein Projekt), wird gar nicht angeboten -- ein grauer
 *    Eintrag ist eine Antwort auf eine Frage, die niemand gestellt hat.
 *  * **Ohne Frage die ganze Liste, in ihrer eigenen Reihenfolge.** Das leere
 *    Feld ist die Frage „was kann ich hier überhaupt“, und darauf ist die
 *    Antwort keine Rangliste.
 */
/**
 * Auf das Wesentliche gebracht: klein, ohne Umlaute, ohne Satzzeichen.
 *
 * Und zwar **vor** der Zerlegung in Wörter: „ß“ wird zu „ss“, und ein Bindestrich
 * trennt („Ton-Werkzeuge“ sind zwei Wörter). Sonst fände „werkzeug“ den Befehl
 * nicht am Wortanfang.
 */
export function vereinfache(text) {
    return text
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * Wie gut ein Stück Text zu einem gesuchten Wort passt.
 *
 * 3 -- der Text fängt damit an; 2 -- ein Wort darin fängt damit an; 1 -- es
 * steht irgendwo darin; 0 -- gar nicht.
 */
function passt(text, wort) {
    if (!wort)
        return 0;
    if (text.startsWith(wort))
        return 3;
    if (text.split(' ').some((w) => w.startsWith(wort)))
        return 2;
    return text.includes(wort) ? 1 : 0;
}
/**
 * Die Befehle, die zu einer Frage passen -- die besten zuerst.
 *
 * Bei gleichem Rang bleibt die Reihenfolge der Liste: sie ist selbst eine
 * Aussage darüber, was häufiger gebraucht wird, und eine Rangliste, die bei
 * Gleichstand würfelt, macht dasselbe Feld zweimal anders.
 */
export function sucheBefehle(befehle, frage) {
    const moeglich = befehle.filter((b) => b.moeglich !== false);
    const worte = vereinfache(frage).split(' ').filter(Boolean);
    if (worte.length === 0)
        return moeglich.map((befehl) => ({ befehl, punkte: 0 }));
    const treffer = [];
    for (const befehl of moeglich) {
        const name = vereinfache(befehl.name);
        const stichworte = (befehl.stichworte ?? []).map(vereinfache);
        let punkte = 0;
        let alleDa = true;
        for (const wort of worte) {
            const imNamen = passt(name, wort);
            // Ein Stichwort zählt weniger als der Name -- aber mehr als nichts.
            const imStichwort = Math.max(0, ...stichworte.map((s) => passt(s, wort)));
            const beste = Math.max(imNamen * 2, imStichwort);
            if (beste === 0) {
                alleDa = false;
                break;
            }
            punkte += beste;
        }
        if (alleDa)
            treffer.push({ befehl, punkte });
    }
    // `sort` ist in JavaScript stabil -- bei gleichen Punkten bleibt die
    // Reihenfolge der Liste, und genau darauf wird sich hier verlassen.
    return treffer.sort((a, b) => b.punkte - a.punkte);
}
//# sourceMappingURL=befehle.js.map